<template>
  <div>
    <div v-if="waSystemToken" class="text-white absolute right-3 top-3">
      <v-btn color="red" text @click="confirmDisconnectFbAccount = true"
        >Disconnect facebook account</v-btn
      >
    </div>
    <button
      v-else
      class="px-2 rounded-lg py-1 my-4 absolute right-5 top-0"
      :style="{ backgroundColor: '#1877F2' }"
      @click="onLoginWithFb"
    >
      <v-icon class="white--text mr-1">mdi-facebook</v-icon>
      <span class="text-white font-semibold text-sm">
        Login with Facebook
      </span>
    </button>
    <div class="p-5 pt-0 overflow-auto max-h-[70vh]">
      <div
        class="border border-blue-200 relative bg-white dark-bg-default p-3 relative"
      >
        <IntegrationInfo v-if="!waSystemToken" />
        <div>
          <template v-if="businessNumbers.length">
            <p class="gradient-label mb-0">
              Phone number connected with
              <span class="font-bold">WhatsApp</span> integration
            </p>
            <v-radio-group class="-mb-4" v-model="waBusinessNumber">
              <v-radio
                v-for="n in businessNumbers"
                :key="n.id"
                :label="`${n.display_phone_number} (${n.verified_name})`"
                :value="n.id"
              ></v-radio>
            </v-radio-group>
          </template>
        </div>
        <div
          v-if="!!waBusinessNumber"
          v-show="isFeatureVisible('WHATS_APP_BOT')"
        >
          <div class="absolute top-3 right-3">
            <v-btn
              v-if="botFlows.length < 3"
              @click="showBotBuilderDialog = true"
              elevation="2"
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text"
              >Add bot flow</v-btn
            >
          </div>
          <template v-if="botFlows.length">
            <v-divider class="my-4"></v-divider>
            <p>Chatbot Templates</p>
          </template>
          <div>
            <div
              v-for="flow in botFlows"
              :key="flow.id"
              class="flex items-center justify-between"
            >
              <span class="font-semibold text-gray-500"
                >Flow #{{ flow._id.slice(20).toUpperCase() }}</span
              >
              <span
                class="font-semibold text-green-700 ml-2"
                v-if="flow._id === activeBotFlow"
                >ACTIVE</span
              >
              <div class="flex align-center gap-3 justify-end">
                <v-btn
                  v-if="flow._id !== activeBotFlow"
                  @click="setActiveBotFlow(flow)"
                  elevation="2"
                  class="mb-4"
                  >Set as default</v-btn
                >
                <v-btn
                  @click="onEditFlow(flow)"
                  elevation="2"
                  class="mb-4"
                  v-show="isFeatureVisible('CUSTOMISE_BOT_FLOW')"
                  >Edit</v-btn
                >
                <v-btn
                  @click="onFlowDelete(flow)"
                  elevation="2"
                  class="mb-4"
                  color="error"
                  v-show="isFeatureVisible('CUSTOMISE_BOT_FLOW')"
                  >Delete</v-btn
                >
              </div>
            </div>
          </div>
        </div>
        <div>
          <v-divider class="my-4"></v-divider>
          <div class="flex mb-4 items-center justify-between">
            <p class="text-lg gradient-label font-bold text-gray-700">
              Case Outcome Templates
            </p>
            <v-btn @click="onAddAppTemplate" color="primary" variant="tonal">
              <v-icon class="mr-2">mdi-plus</v-icon>
              Add Template
            </v-btn>
          </div>
          <div v-if="appTemplates.length" class="grid grid-cols-3 gap-4">
            <div
              v-for="(t, i) in appTemplates"
              :key="i"
              class="flex-col flex items-between gap-2 justify-start border shadow-sm py-2 px-3 relative"
            >
              <div class="flex flex-col gap-1">
                <p class="font-semibold text-gray-600 mb-0">{{ t.name }}</p>
                <p class="text-gray-700 text-sm mb-0">{{ t.templateTxt }}</p>
              </div>
              <div class="flex items-end justify-between">
                <p
                  class="font-semibold text-xs mb-0 pb-0.5 text-gray-700"
                  :class="getStatusColor(t.status)"
                >
                  {{ t.status }}
                </p>
                <div class="flex items-center justify-end gap-2">
                  <v-btn
                    icon
                    :color="$vuetify.theme.currentTheme.primary"
                    @click="onTemplateEdit(t.indx)"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                  <v-btn icon color="red" @click="onTemplateDelete(t.indx)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
        <TemplateTriggers
          v-if="!!waBusinessNumber"
          @triggerChange="updateTemplateTrigger"
          @addTemplateTrigger="onAddTrigger"
          :templateTriggers="templateTriggers"
          :availableTemplates="availableTemplates"
        />
        <div v-if="availableTemplates.length">
          <v-divider class="my-4"></v-divider>
          <div class="flex items-center justify-between">
            <p class="text-lg gradient-label font-bold text-gray-700">
              Message Templates
            </p>
            <div class="flex items-center justify-end gap-2 mb-4">
              <div>
                <div
                  @click="onRefreshTemplates"
                  class="flex items-center justify-center cursor-pointer h-10 w-10 border border-gray-300 shadow-sm"
                >
                  <Loader v-if="fetchingTemplates" />
                  <v-icon v-else color="blue">mdi-replay</v-icon>
                </div>
              </div>
              <div class="w-48">
                <SelectComponent
                  label="Status"
                  :items="templateStatuses"
                  v-model="filters.status"
                />
              </div>
            </div>
          </div>
          <div
            v-if="!filteredTemplates.length"
            class="flex items-center justify-center"
          >
            <p class="text-gray-500 mt-8 pb-8">
              No templates found with the current filters
            </p>
          </div>
          <div v-else class="grid grid-cols-3 gap-4">
            <div
              v-for="(t, i) in filteredTemplates"
              :key="i"
              class="flex-col flex items-between gap-2 justify-start border shadow-sm py-2 px-3 relative"
            >
              <div class="flex flex-col gap-1">
                <p class="font-semibold text-gray-600 mb-0">{{ t.name }}</p>
                <p class="text-gray-700 text-sm mb-0">{{ t.templateTxt }}</p>
              </div>
              <div class="flex items-end justify-between">
                <p
                  class="font-semibold text-xs mb-0 pb-0.5 text-gray-700"
                  :class="getStatusColor(t.status)"
                >
                  {{ t.status }}
                </p>
                <div class="flex items-center justify-end gap-2">
                  <v-btn
                    icon
                    :color="$vuetify.theme.currentTheme.primary"
                    @click="onTemplateEdit(i)"
                  >
                    <v-icon>mdi-square-edit-outline</v-icon>
                  </v-btn>
                  <v-btn icon color="red" @click="onTemplateDelete(i)">
                    <v-icon>mdi-trash-can</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <v-dialog
        v-model="isBusinessIdDialogVisible"
        persistent
        max-width="500"
        :color="$vuetify.theme.currentTheme.navigationColor"
      >
        <div>
          <div class="p-4 bg-white dark-bg-custom">
            <v-text-field
              focused
              v-model="waBussinessId"
              label="Whatsapp Business Id"
            ></v-text-field>
            <div class="flex items-center gap-2 justify-end">
              <v-btn
                @click="isBusinessIdDialogVisible = false"
                outlined
                elevation="2"
                color="red"
                >Cancel</v-btn
              >
              <v-btn
                @click="onAddBusinessId"
                elevation="2"
                :loading="addingBusinessId"
                :color="$vuetify.theme.currentTheme.primary"
                class="white--text"
                >Continue</v-btn
              >
            </div>
          </div>
        </div>
      </v-dialog>
      <v-dialog
        v-model="showBotBuilderDialog"
        persistent
        fullscreen
        hide-overlay
        :color="$vuetify.theme.currentTheme.navigationColor"
      >
        <iframe
          @load="onIframeLoaded"
          ref="botFlow"
          src="https://superadmin.myfinfi.com/admin/bot-builder"
          width="100%"
          height="100%"
        ></iframe>
      </v-dialog>
      <v-dialog
        v-model="showBotBuilderDialog"
        persistent
        fullscreen
        hide-overlay
        :color="$vuetify.theme.currentTheme.navigationColor"
      >
        <iframe
          @load="onIframeLoaded"
          ref="botFlow"
          src="https://bot-builder.myfinfi.com/admin/bot-builder"
          width="100%"
          height="100%"
        ></iframe>
      </v-dialog>
      <v-dialog
        v-model="editTemplateParamsVisible"
        persistent
        width="450px"
        :color="$vuetify.theme.currentTheme.navigationColor"
      >
        <div class="p-4 bg-white dark-bg-custom">
          <v-select
            v-for="(t, i) in templateParams"
            :key="i"
            :items="getTemplateParamOptions()"
            v-model="templateTriggers[templateParamsEditIndx].params[i]"
            :label="`Select param value for ${templateParams[i]}`"
            item-text="label"
            item-value="value"
            dense
            outlined
            :clearable="true"
          ></v-select>
          <div class="flex gap-2 items-center justify-end">
            <v-btn
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text"
              @click="editTemplateParamsVisible = false"
            >
              <span class="text-white"> Done </span>
            </v-btn>
          </div>
        </div>
      </v-dialog>
      <v-dialog
        v-model="addTemplateTriggerDialogVisible"
        persistent
        width="450px"
      >
        <div class="p-4 bg-white dark-bg-custom">
          <v-select
            :items="templateTriggerTypes"
            v-model="newTemplateTrigger"
            label="Select template trigger"
            item-text="title"
            item-value="type"
            dense
            outlined
            :clearable="true"
          ></v-select>
          <v-select
            :items="availableTriggerTemplates"
            v-model="newTemplateTriggerTemplate"
            label="Select template"
            item-text="name"
            item-value="name"
            dense
            outlined
            :clearable="true"
          ></v-select>
          <div class="flex gap-2 items-center justify-end">
            <v-btn outlined @click="addTemplateTriggerDialogVisible = false">
              <span> Cancel </span>
            </v-btn>
            <v-btn
              :color="$vuetify.theme.currentTheme.primary"
              class="white--text"
              :loading="addingTrigger"
              @click="handleAddTemplateTrigger"
            >
              <span class="text-white"> Add </span>
            </v-btn>
          </div>
        </div>
      </v-dialog>
      <v-dialog v-model="addTemplateDialogVisible" persistent width="450px">
        <div class="p-4 bg-white dark-bg-custom">
          <v-text-field
            v-if="!editTemplateDialogVisible"
            dense
            outlined
            v-model="templateName"
            label="Enter template name"
          >
          </v-text-field>
          <div class="relative">
            <Mentionable :keys="['@']" :items="availableParamsList">
              <v-textarea
                v-model="templateBody"
                outlined
                label="Template text"
                @input="handleTemplateTextInput"
                class="mentionable-textarea"
              >
              </v-textarea>

              <template #no-result>
                <div class="p-2 text-sm text-gray-500">No result</div>
              </template>
              <template #item-@="{ item }">
                <div
                  class="p-2 text-gray-700 dark-text-color hover:bg-gray-100 cursor-pointer"
                >
                  {{ item.label }}
                </div>
              </template>
            </Mentionable>
            <div class="absolute right-1 top-0">
              <v-tooltip left>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    size="20"
                    class="text-gray-500 hover:text-gray-700"
                    >mdi-help-circle-outline</v-icon
                  >
                </template>
                <div class="flex py-2 flex-col gap-2">
                  <p class="font-semibold text-white dark-text-color mb-0">
                    Available params
                  </p>
                  <div
                    v-for="(t, i) in availableParamsList"
                    :key="i"
                    class="bg-white dark-bg-custom rounded-md p-1 px-1.5 flex flex-col items-start justify-center"
                  >
                    <span class="text-sm font-semibold text-gray-700">
                      {{ t.label }}
                    </span>
                    <span class="text-xs text-gray-700"> @{{ t.value }} </span>
                    <span class="text-xs text-gray-500">
                      {{ t.description }}
                    </span>
                  </div>
                </div>
              </v-tooltip>
            </div>
          </div>
          <div class="flex gap-2 items-center justify-end">
            <v-btn color="red" outlined @click="onCancelAddTemplate">
              <span> Cancel </span>
            </v-btn>
            <v-btn
              :loading="addingTemplate"
              :color="$vuetify.theme.currentTheme.primary"
              @click="handleAddTemplate"
            >
              <span v-if="editTemplateDialogVisible" class="text-white">
                Edit Template
              </span>
              <span v-else class="text-white"> Add Template </span>
            </v-btn>
          </div>
        </div>
      </v-dialog>
      <AlertPopUp
        :showConfirmationDialog="showDeleteTriggerDialog"
        :confirmationTitle="'Do you want to delete this template?'"
        :confirmationMessage="''"
        :confirmationActionText="'Delete'"
        :confirmationActionColor="'red'"
        :performAction="deleteTemplateTrigger"
        :cancel="displayDeleteDialog"
      />
      <AlertPopUp
        :showConfirmationDialog="confirmDisconnectFbAccount"
        :confirmationTitle="'Do you want to disconnect?'"
        :confirmationMessage="'This will remove all your configurations from our platform'"
        :confirmationActionText="'Ok'"
        :confirmationActionColor="'red'"
        :performAction="handleDisconnectFacebookAccount"
        :cancel="onDisconnectFbAccount"
      />
      <AlertPopUp
        :showConfirmationDialog="templateDeleteDialogVisible"
        :confirmationTitle="'Do you want to delete this template?'"
        :confirmationMessage="''"
        :confirmationActionText="'Ok'"
        :confirmationActionColor="'red'"
        :performAction="deleteWhatsappTemplate"
        :cancel="deleteTemplate"
        :loading="deletingTemplate"
      />
      <AlertPopUp
        :showConfirmationDialog="confirmDeleteBotFlow"
        :confirmationTitle="'Do you want to delete this flow?'"
        :confirmationMessage="''"
        :confirmationActionText="'Ok'"
        :confirmationActionColor="'red'"
        :performAction="deleteBotFlow"
        :cancel="deleteFlow"
      />
    </div>
  </div>
</template>
<script>
import { isModuleFeatureAllowed } from "@/utils/common";
import { debounce } from "@/utils/common";
import { Mentionable } from "vue-mention";
import AlertPopUp from "@/components/AlertPopUp";
import SelectComponent from "@/components/UI/Select.vue";
import TemplateTriggers from "./whatsapp/TemplateTriggers";
import Loader from "@/components/UI/Loader.vue";
import IntegrationInfo from "./whatsapp/IntegrationInfo";

export default {
  components: {
    Mentionable,
    IntegrationInfo,
    SelectComponent,
    TemplateTriggers,
    Loader,
    AlertPopUp,
  },
  data() {
    return {
      authData: this.$storage.getUniversal("token"),
      orgCode: this.$storage.getUniversal("organization_code"),
      templateTriggers: [],
      bindTemplateRes: [],
      templateParams: [],
      whatsappTemplates: [],
      addingTemplate: false,
      addingTrigger: false,
      addTemplateTriggerDialogVisible: false,
      addTemplateDialogVisible: false,
      editTemplateDialogVisible: false,
      newTemplateTrigger: "",
      newTemplateTriggerCategory: "",
      newTemplateTriggerTemplate: "",
      templateName: "",
      templateBody: "",
      newTemplateParams: [],
      selectedTemplateType: "",
      waBusinessNumber: "",
      waBussinessId: "",
      waSystemToken: "",
      activeBotFlow: null,
      businessNumbers: [],
      confirmDisconnectFbAccount: false,
      isDefaultWhatsappFlow: true,
      isBusinessIdDialogVisible: false,
      addingBusinessId: false,
      showBotBuilderDialog: false,
      templateTriggerTypes: [],
      templateTriggerRest: [],
      botFlows: [],
      botFlow: null,
      confirmDeleteBotFlow: false,
      templateParamsEditIndx: null,
      deletingTemplate: false,
      editTemplateParamsVisible: false,
      templateDeleteDialogVisible: false,
      templateDeleteIndx: null,
      editTemplateIndx: null,
      showDeleteTriggerDialog: false,
      triggerDeleteIndex: null,
      fetchingTemplates: false,
      filters: {
        status: "",
      },
    };
  },
  props: {
    receivedData: {
      type: Object,
      default: null,
    },
  },
  computed: {
    savedBusinessId() {
      return this.receivedData?.applications?.whatsapp?.waBussinessId;
    },
    templateStatuses() {
      return [
        { label: "All", value: "" },
        { label: "Approved", value: "APPROVED" },
        { label: "Rejected", value: "REJECTED" },
        { label: "Pending", value: "PENDING" },
      ];
    },
    availableTriggers() {
      return ["task_end", "task_assign"];
    },
    availableTemplateTriggers() {
      const availableTriggers = [
        {
          type: "task_end",
          title: "Task End",
          template: "",
          availableParams: ["assigned_to"],
          params: [],
          modified: false,
        },
        {
          type: "task_end_agent",
          title: "Task End Agent",
          template: "",
          availableParams: ["assigned_to"],
          params: [],
          modified: false,
        },
        {
          type: "task_end_customer",
          title: "Task End Customer",
          template: "",
          availableParams: ["assigned_to"],
          params: [],
          modified: false,
        },
        {
          type: "task_assign",
          title: "Task Assign",
          template: "",
          availableParams: ["task_title"],
          params: [],
          modified: false,
        },
        {
          type: "amc_notification",
          title: "AMC Notification",
          template: "",
          availableParams: ["amc_data"],
          params: [],
          modified: false,
        },
        {
          type: "tele_caller_notification_agent",
          title: "Call End Agent",
          template: "",
          availableParams: ["assigned_to"],
          params: [],
          modified: false,
        },
        {
          type: "tele_caller_notification_customer",
          title: "Call End Customer",
          template: "",
          availableParams: ["assigned_to"],
          params: [],
          modified: false,
        },
      ];

      return availableTriggers;
    },
    filteredTemplates() {
      if (!this.filters.status) return this.availableTemplates;

      return this.availableTemplates.filter(
        (t) => t.status === this.filters.status
      );
    },
    availableTemplates() {
      const savedTObj = this.bindTemplateRes.reduce((acc, curr) => {
        acc[curr.templateName] = curr;
        return acc;
      }, {});

      return this.whatsappTemplates.map((curr) => {
        const template = savedTObj[curr.name];
        const templateTxt = curr.components.find((c) => c.type === "BODY").text;
        return { ...curr, params: template?.params || [], templateTxt };
      }, []);
    },
    appTemplates() {
      let currIndx = 0;
      const appTemplates = this.availableTemplates.reduce(
        (templates, template) => {
          if (template.triggerCategory === "app_template")
            templates.push({ ...template, indx: currIndx });

          currIndx++;
          return templates;
        },
        []
      );

      return appTemplates;
    },
    approvedTemplates() {
      const savedTObj = this.bindTemplateRes.reduce((acc, curr) => {
        acc[curr.templateName] = curr;
        return acc;
      }, {});
      return this.whatsappTemplates.map((t) => {
        if (t.status === "APPROVED") {
          const template = savedTObj[t.name];
          return { ...t, params: template?.params || [] };
        }
      });
    },
    availableTriggerTemplates() {
      const savedTObj = this.templateTriggers.reduce((acc, curr) => {
        acc[curr.template] = curr;
        return acc;
      }, {});
      return this.whatsappTemplates.map((t) => {
        const template = savedTObj[t.name];

        if (t.status === "APPROVED" && template?.template !== t.name) {
          return { ...t, params: template?.params || [] };
        }
      });
    },
    availableParamsList() {
      const categories = {
        task: [
          {
            label: "Task End Time",
            value: "task_end_time",
            description: "Recorded time when the task is completed",
            eg: "15/08/2024",
          },
          {
            label: "Assigned To",
            value: "assigned_to",
            description: "The user who is assigned to the task",
            eg: "John Doe",
          },
          {
            label: "Task title",
            value: "task_title",
            description: "The title of the task",
            eg: "Customer complaint",
          },
        ],
        notification: [
          {
            label: "AMC stats",
            value: "amc_data",
            description: "AMC data",
            eg: "Company name - Plan name - Task title",
          },
        ],
        app_template: [
          {
            label: "Task End Time",
            value: "task_end_time",
            description: "Recorded time when the task is completed",
            eg: "15/08/2024",
          },
          {
            label: "Assigned To",
            value: "assigned_to",
            description: "The user who is assigned to the task",
            eg: "John Doe",
          },
          {
            label: "Task title",
            value: "task_title",
            description: "The title of the task",
            eg: "Customer complaint",
          },
        ],
        ["tele-caller"]: [
          {
            label: "Task End Time",
            value: "task_end_time",
            description: "Recorded time when the task is completed",
            eg: "15/08/2024",
          },
          {
            label: "Assigned To",
            value: "assigned_to",
            description: "The user who is assigned to the task",
            eg: "John Doe",
          },
          {
            label: "Task title",
            value: "task_title",
            description: "The title of the task",
            eg: "Customer complaint",
          },
        ],
      };

      return categories[this.newTemplateTriggerCategory?.category] || [];
    },
    addTriggerBtn() {
      const addedTriggers = this.templateTriggers.reduce((prev, curr) => {
        prev[curr.type] = curr;
        return prev;
      }, {});
      const isAllTriggersAdded =
        Object.keys(addedTriggers).length === this.availableTriggers.length;
      if (isAllTriggersAdded) return false;

      return true;
    },
  },
  methods: {
    async onAddBusinessId() {
      if (!this.waBussinessId) {
        this.$toast.error("Please add business id");
        return;
      }

      this.addingBusinessId = true;

      try {
        const payload = {
          applications: {
            ...this.receivedData.applications,
            whatsapp: {
              ...this.receivedData.applications.whatsapp,
              waBussinessId: this.waBusinessId,
            },
          },
        };

        await this.$axios.post("/workforce/org/config", payload, {
          headers: {
            Authorization: this.authData.access_token,
          },
        });

        this.loginWithFb();
      } catch (err) {
        this.$toast.error("Error adding business id");
      }

      this.isBusinessIdDialogVisible = false;
      this.addingBusinessId = false;
    },
    deleteFlow() {
      this.confirmDeleteBotFlow = false;
    },
    deleteTemplate() {
      this.templateDeleteDialogVisible = false;
    },
    onDisconnectFbAccount() {
      this.confirmDisconnectFbAccount = false;
    },
    getStatusColor(status) {
      if (status === "REJECTED") {
        return "text-red-700";
      } else if (status === "APPROVED" || status === "ACTIVE") {
        return "text-green-700";
      } else if (status === "PENDING") {
        return "text-orange-700";
      }
    },
    onCancelAddTemplate() {
      this.addTemplateDialogVisible = false;
      this.templateName = "";
      this.selectedTemplateType = "";
      this.templateBody = "";
      this.editTemplateDialogVisible = false;
    },
    onAddAppTemplate() {
      this.addTemplateDialogVisible = true;
      this.newTemplateTrigger = {};
      this.newTemplateTriggerCategory = {
        category: "app_template",
        triggers: [],
        title: "App Template",
      };
    },
    onAddTrigger({ category, trigger }) {
      this.addTemplateDialogVisible = true;
      this.newTemplateTrigger = trigger;
      this.newTemplateTriggerCategory = category;
    },
    handleTemplateTextInput: debounce(function () {
      const params = this.templateBody.match(/{{\w+}}/g);
      this.newTemplateParams = params.map((p, i) => {
        if (this.newTemplateParams[i]) return this.newTemplateParams[i];
        return p.replace("{{", "").replace("}}", "");
      });
    }, 1000),
    onRefreshTemplates() {
      if (this.fetchingTemplates) return;
      this.getBindedTemplates();
      this.fetchTemplates(true);
    },
    async handleAddTemplateTrigger() {
      try {
        const payload = {
          templateType: this.newTemplateTrigger,
          params: payload.params || [],
          languageCode: payload.languageCode,
          templateName: payload.templateName,
        };
        this.addingTrigger = true;

        this.$axios.post(`/workforce/whatsapp/templates/assign`, payload, {
          headers: {
            Authorization: this.authData.access_token,
          },
        });
        this.getBindedTemplates();
        this.templateTriggers.push({
          type: this.newTemplateTrigger,
          template: payload.templateName,
          params: payload.params,
          modified: false,
        });
        this.newTemplateTrigger = "";
        this.newTemplateTriggerTemplate = "";
        this.addTemplateTriggerDialogVisible = false;
        this.$toast.success("Trigger added successfully!");
      } catch {
        this.$toast.error("Error adding trigger");
      }

      this.addingTrigger = false;
    },
    displayDeleteDialog(i) {
      this.showDeleteTriggerDialog = !this.showDeleteTriggerDialog;
      this.triggerDeleteIndex = i;
    },
    async deleteTemplateTrigger() {
      let i = this.triggerDeleteIndex;
      const trigger = this.templateTriggers[i];
      this.templateTriggers.splice(i, 1);
      try {
        await this.$axios.delete(
          `/workforce/whatsapp/templates/assign/${trigger.type}`,
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.$toast.success("Trigger deleted successfully!");
        this.templateDeleteDialogVisible = false;
        this.fetchTemplates();
      } catch {
        this.$toast.error("Error deleting trigger");
      }
      this.showDeleteTriggerDialog = false;
    },
    async updateTemplateTrigger({ trigger, template, category }) {
      try {
        const payload = {
          category: category.category,
          templateType: trigger.type,
          params: template.params,
          languageCode: "en",
          templateName: template.name,
          templateText: template.templateTxt,
          templateId: template.id,
        };

        await this.$axios.post(
          `/workforce/whatsapp/templates/assign`,
          payload,
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.getBindedTemplates();
        this.$toast.success("Updated successfully!");
      } catch (err) {
        this.$toast.error("Error updating config!");
      }
    },
    handleTemplateChange(i) {
      const prevData = this.bindTemplateRes.find(
        (t) => t.templateType === this.templateTriggers[i].type
      );
      this.templateTriggers[i].modified =
        prevData?.templateName !== this.templateTriggers[i].template;
    },
    async deleteWhatsappTemplate() {
      const template = this.whatsappTemplates[this.templateDeleteIndx];
      this.deletingTemplate = true;
      try {
        await this.$axios.delete(
          `/workforce/whatsapp/templates/${template.name}`,
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.$toast.success("Template deleted successfully!");
        this.templateDeleteDialogVisible = false;
        this.templateDeleteIndx = null;
        this.fetchTemplates();
      } catch {
        this.$toast.error("Error deleting template");
      }

      this.deletingTemplate = false;
    },
    onTemplateEdit(i) {
      const template = this.availableTemplates[i];
      const templateText = template.components.find(
        (c) => c.type === "BODY"
      ).text;

      this.addTemplateDialogVisible = true;
      this.editTemplateDialogVisible = true;
      this.handleTemplateTextInput();
      this.editTemplateIndx = i;
      this.newTemplateTriggerCategory = {
        category: this.filteredTemplates[i].triggerCategory,
      };

      this.templateBody = templateText
        .split(/\s+/)
        .map((s) => {
          if (/{{\d}}/.test(s)) {
            const indx = s
              .match(/{{\d}}/)[0]
              .replace("{{", "")
              .replace("}}", "");
            return "@" + template.params[indx - 1];
          } else return s;
        })
        .join(" ");
    },
    onTemplateDelete(i) {
      this.templateDeleteDialogVisible = true;
      this.templateDeleteIndx = i;
    },
    async getBindedTemplates() {
      const { data } = await this.$axios.get(
        "/workforce/whatsapp/templates/assign",
        {
          headers: {
            Authorization: this.authData.access_token,
          },
        }
      );
      this.bindTemplateRes = data.templates;
    },
    getTemplateParamOptions() {
      const fields = {
        task_end: [
          { label: "Task End Time", value: "task_end_time" },
          { label: "Assigned To", value: "assigned_to" },
        ],
        task_assign: [{ label: "Task title", value: "task_title" }],
      };

      return fields[this.templateTriggers[this.templateParamsEditIndx].type];
    },
    onEditTemplateParams(i) {
      const t = this.templateTriggers[i];
      const template = this.whatsappTemplates.find(
        (template) => template.name === t.template
      );
      if (!template) return [];
      const bodyComponent = template.components.find((c) => c.type === "BODY");
      const params = bodyComponent.text.match(/{{\d}}/g);
      this.editTemplateParamsVisible = true;
      this.templateParams = params;
      this.templateParamsEditIndx = i;
    },
    templateHasParams(t) {
      const template = this.whatsappTemplates.find(
        (template) => template.name === t.template
      );
      if (!template) return false;
      const bodyComponent = template.components.find((c) => c.type === "BODY");
      const hasParams = /{{\d}}/.test(bodyComponent.text);
      return hasParams;
    },
    async handleAddTemplate() {
      const isNew = !this.editTemplateDialogVisible;

      if (!this.templateName && isNew) {
        this.$toast.error("Please add templateName");
        return;
      }

      if (!this.templateBody) {
        this.$toast.error("Please add templateBody");
        return;
      }
      const params = this.templateBody
        .split(/\s+/)
        .filter((s) => s.includes("@"))
        .map((p) => p.replace("@", ""));
      let t = this.templateBody;

      params.forEach((p, i) => {
        t = t.replace(`@${p}`, `{{${i + 1}}}`);
      });

      const payload = {
        templateTriggerCategory: this.newTemplateTriggerCategory.category,
        templateText: t,
        params,
      };

      if (isNew) {
        payload.languageCode = "en";
        payload.templateName = this.templateName;
      }

      payload.example = params.map((p) => {
        const param = this.availableParamsList.find((param) => {
          return param.value === p;
        });

        if (param) return param.eg;

        return "";
      });

      try {
        this.addingTemplate = true;
        const fn = isNew ? this.$axios.post : this.$axios.put;
        let url = "/workforce/whatsapp/templates";

        if (!isNew) {
          const t = this.whatsappTemplates[this.editTemplateIndx];
          const templateId = t.id;
          url += `/${templateId}`;
        }

        await fn(url, payload, {
          headers: {
            Authorization: this.authData.access_token,
          },
        });

        this.$toast.success(
          `Template ${isNew ? "added" : "updated"} successfully!`
        );
        if (isNew) {
          this.templateName = "";
          this.selectedTemplateType = "";
        } else {
          this.editTemplateDialogVisible = false;
        }
        this.addTemplateDialogVisible = false;
        this.templateBody = "";
        this.fetchTemplates();
      } catch (err) {
        console.log(err);
        this.$toast.error("Could not add template");
      }

      this.addingTemplate = false;
    },
    isFeatureVisible(feature) {
      return isModuleFeatureAllowed("ADDONS", feature);
    },
    refetchData() {
      this.$emit("refetchData");
    },
    onIframeLoaded() {
      if (this.botFlow) {
        this.sendMessageToBotFlow({
          type: "LOAD_FLOW",
          payload: this.botFlow.flow,
        });
      }
    },
    onEditFlow(flow) {
      this.showBotBuilderDialog = true;
      this.botFlow = flow;
      this.sendMessageToBotFlow({
        type: "LOAD_FLOW",
        payload: this.botFlow.flow,
      });
    },
    sendMessageToBotFlow(message) {
      const iframe = this.$refs.botFlow;
      if (!iframe) return;
      iframe.contentWindow.postMessage(
        message,
        "https://bot-builder.myfinfi.com"
      );
    },
    async fetchTemplates(refetch = false) {
      this.fetchingTemplates = true;
      const { data } = await this.$axios.get("/workforce/whatsapp/templates", {
        headers: {
          Authorization: this.authData.access_token,
        },
      });

      this.whatsappTemplates = data.templates.filter((t) => t.name);
      this.fetchingTemplates = false;

      if (refetch) this.$toast.success("Templates refreshed successfully");
    },
    async setActiveBotFlow(flow) {
      try {
        const payload = {
          applications: {
            ...this.receivedData.applications,
            whatsapp: {
              ...this.receivedData.applications.whatsapp,
              activeBotFlow: flow._id,
            },
          },
        };

        await this.$axios.post("/workforce/org/config", payload, {
          headers: {
            Authorization: this.authData.access_token,
          },
        });

        this.$toast.success("Activated bot flow successfully");
        this.restartBotService();
        this.refetchData();
      } catch (err) {
        this.$toast.error("Error activating bot flow");
      }
    },
    onFlowDelete(flow) {
      if (flow._id === this.activeBotFlow) {
        return this.$toast.error("Cannot delete the active bot flow");
      }
      this.confirmDeleteBotFlow = true;
    },
    async onFlowSaved(payload) {
      this.showBotBuilderDialog = false;
      if (this.botFlow) {
        await this.updateBotFlow(payload);
        this.botFlow = null;
      } else {
        await this.addBotFlow(payload);
      }
      this.refetchData();
      this.restartBotService();
    },
    handleMessage(event, _this) {
      if (event.origin !== "https://bot-builder.myfinfi.com") {
        return;
      }

      const { type, payload } = event.data;

      if (type === "CANCEL") {
        _this.showBotBuilderDialog = false;
      } else if (type === "SAVE") {
        _this.onFlowSaved(payload);
      }
    },
    async handleDisconnectFacebookAccount() {
      try {
        await this.$axios.delete(
          "/workforce/org/config/facebook-access-token",
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.botFlow = null;
        await this.restartBotService();
        this.$toast.success("Account successfully removed!");
        window.location.reload();
      } catch (err) {
        this.$toast.error("Error removing account");
      }
    },
    async deleteBotFlow() {
      try {
        await this.$axios.delete(
          "/workforce/org/config/bot-flow/" + this.activeBotFlow,
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.$toast.success("Bot flow deleted successfully");
        this.activeBotFlow = null;
        this.confirmDeleteBotFlow = false;
        this.refetchData();
        this.restartBotService();
      } catch (err) {
        this.$toast.error("Error deleting bot flow");
      }
    },
    async updateBotFlow(payload) {
      try {
        await this.$axios.put(
          "/workforce/org/config/bot-flow/" + this.botFlow._id,
          payload,
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.$toast.success("Bot flow updated successfully");
        this.refetchData();
        this.restartBotService();
      } catch (err) {
        this.$toast.error("Error updating bot flow");
      }
    },
    onLoginWithFb() {
      this.isBusinessIdDialogVisible = true;
    },
    loginWithFb() {
      const _this = this;
      window.FB.login(
        async (res) => {
          if (res.authResponse?.code) {
            await _this.saveFbTokenByCode(res.authResponse.code);

            _this.restartBotService();
          } else {
            _this.$toast.error("Error logging in with FaceBook");
          }
        },
        {
          response_type: "code",
          override_default_response_type: true,
          config_id: "439111278760748",
        }
      );
    },
    async addBotFlow(payload) {
      try {
        await this.$axios.post("/workforce/org/config/bot-flow", payload, {
          headers: {
            Authorization: this.authData.access_token,
          },
        });
        this.$toast.success("Bot flow added successfully");
        this.refetchData();
        this.restartBotService();
      } catch (err) {
        this.$toast.error("Error adding bot flow");
      }
    },
    async saveFbTokenByCode(code) {
      try {
        await this.$axios.post(
          "/workforce/org/config/facebook-access-token",
          { code },
          {
            headers: {
              Authorization: this.authData.access_token,
            },
          }
        );
        this.$toast.success("Login successfull!");
      } catch (err) {
        this.$toast.error("Error getting token");
      }
    },
    async restartBotService() {
      await this.$axios.post("https://bot.myfinfi.com/re-init-configs", {
        orgCode: this.orgCode,
      });
    },
  },
  beforeDestroy() {
    window.removeEventListener("message", (e) => this.handleMessage(e, this));
  },
  watch: {
    bindTemplateRes(newVal) {
      const availableTriggers = this.availableTemplateTriggers;

      const triggers = newVal.reduce((acc, curr) => {
        if (curr.templateType) {
          const trigger = availableTriggers.find(
            (t) => t.type === curr.templateType
          );

          acc.push({
            ...trigger,
            template: curr.templateName || "",
            params: curr.params || [],
          });
        }

        return acc;
      }, []);

      this.templateTriggerTypes = availableTriggers.reduce((acc, curr) => {
        if (!triggers.find((t) => t.type === curr.type)) {
          acc.push(curr);
        }

        return acc;
      }, []);
      this.templateTriggers = triggers;
    },
    savedBusinessId: {
      handler(id) {
        if (!id) return;
        this.getBindedTemplates();
        this.fetchTemplates(false);
      },
      immediate: true,
    },
    receivedData: {
      handler(newVal) {
        if (!newVal.applications) return;
        this.waBusinessNumber = newVal.applications?.whatsapp?.waBusinessNumber;
        this.waBussinessId = newVal.applications?.whatsapp?.waBussinessId;
        this.waSystemToken = newVal.applications?.whatsapp?.waSystemToken;
        this.businessNumbers =
          newVal.applications?.whatsapp?.phoneNumbers || [];
        this.botFlows = newVal.applications?.whatsapp?.botFlows || [];
        this.activeBotFlow = newVal.applications?.whatsapp?.activeBotFlow?._id;
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener("message", (e) => this.handleMessage(e, this));
  },
};
</script>
<style></style>

